import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { KieneUtils } from '../../../kiene-utils';
import { FIBU_BWA_GRUPPEN, FIBU_KONTO_TYPEN, FibuKonto, suggestFibuBwaGruppeId } from '../fibu-konto';

@Component({
    selector: 'kiene-fibu-konto-dialog',
    templateUrl: './fibu-konto-dialog.component.html',
    styleUrls: ['./fibu-konto-dialog.component.scss'],
})
export class FibuKontoDialogComponent implements OnInit {
    typen = FIBU_KONTO_TYPEN;
    bwaGruppen = FIBU_BWA_GRUPPEN;
    isEdit = false;

    kontoNrCtrl = new UntypedFormControl('', [Validators.required]);
    bezeichnungCtrl = new UntypedFormControl('', [Validators.required]);
    kontoTypCtrl = new UntypedFormControl(1, [Validators.required]);
    bwaGruppeCtrl = new UntypedFormControl(null);
    sachkontoCtrl = new UntypedFormControl('');
    suffixCtrl = new UntypedFormControl('');
    aktivCtrl = new UntypedFormControl(true);
    kommentarCtrl = new UntypedFormControl('');

    private fibuKontoId: number = null;

    constructor(
        public dialogRef: MatDialogRef<FibuKontoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: { konto: FibuKonto },
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneTableService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        const k = this.data?.konto;
        const kontoId = Number(k?.fibu_konto_id);
        if (k && Number.isFinite(kontoId) && kontoId > 0) {
            this.isEdit = true;
            this.fibuKontoId = kontoId;
            this.kontoNrCtrl.setValue(k.konto_nr);
            this.bezeichnungCtrl.setValue(k.bezeichnung);
            this.kontoTypCtrl.setValue(this.toPositiveId(k.konto_typ) ?? 1);
            this.bwaGruppeCtrl.setValue(this.toPositiveId(k.bwa_gruppe_id));
            this.sachkontoCtrl.setValue(k.sachkonto);
            this.suffixCtrl.setValue(k.kostenstelle_suffix);
            this.aktivCtrl.setValue(Number(k.aktiv) > 0);
            this.kommentarCtrl.setValue(k.kommentar);
        }
    }

    compareId = (a: unknown, b: unknown): boolean => KieneUtils.sameId(a, b);

    onKontoNrBlur() {
        const nr: string = (this.kontoNrCtrl.value || '').trim();
        if (!nr) {
            return;
        }
        if (!this.sachkontoCtrl.value) {
            const punkt = nr.indexOf('.');
            if (punkt > 0) {
                this.sachkontoCtrl.setValue(nr.substring(0, punkt));
                if (!this.suffixCtrl.value) {
                    this.suffixCtrl.setValue(nr.substring(punkt + 1));
                }
            } else {
                this.sachkontoCtrl.setValue(nr);
            }
        }
        if (!this.isEdit || !this.bwaGruppeCtrl.value) {
            const suggested = suggestFibuBwaGruppeId(this.sachkontoCtrl.value || nr);
            if (suggested) {
                this.bwaGruppeCtrl.setValue(suggested);
            }
        }
    }

    isValid(): boolean {
        return this.kontoNrCtrl.valid && this.bezeichnungCtrl.valid && this.kontoTypCtrl.valid;
    }

    save() {
        if (!this.isValid()) {
            return;
        }
        const typ = this.toPositiveId(this.kontoTypCtrl.value) ?? 1;
        const bwaGruppeId = this.toPositiveId(this.bwaGruppeCtrl.value);
        const body: any = {
            konto_nr: (this.kontoNrCtrl.value || '').trim(),
            bezeichnung: (this.bezeichnungCtrl.value || '').trim(),
            konto_typ: typ,
            konto_art: typ === 5 ? 'KREDITOR' : typ === 6 ? 'DEBITOR' : 'SACHKONTO',
            bwa_gruppe_id: bwaGruppeId,
            sachkonto: (this.sachkontoCtrl.value || '').trim() || null,
            kostenstelle_suffix: (this.suffixCtrl.value || '').trim() || null,
            aktiv: this.aktivCtrl.value ? 1 : 0,
            kommentar: (this.kommentarCtrl.value || '').trim() || null,
        };
        if (this.fibuKontoId) {
            body.fibu_konto_id = this.fibuKontoId;
        }

        this.api.post(this.apiBaseUrl + 'fibu_konto/update.php', body).subscribe({
            next: () => {
                this.messageService.infoMessage('Konto gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => this.messageService.errorMessage('Speichern fehlgeschlagen.', err),
        });
    }

    close() {
        this.dialogRef.close(false);
    }

    private toPositiveId(value: unknown): number | null {
        if (value == null || value === '') {
            return null;
        }
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
}
